import { formatSignalType } from "@/features/dashboard/utils/accountsDashboardUtils";
import { Linkedin, Mail, MessageCircle, Phone } from "lucide-react";

export const DASHBOARD_BAR_COLOR = (value) => {
    if (value >= 60) return "#16A34A";
    if (value >= 30) return "#F97316";
    return "#EF4444";
  };

// format percentage
  export const formatDashboardPercent = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "0%";
    const rounded = Math.round(v * 100) / 100;
    const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    return `${s}%`;
  };
  
  // build open rate analytics
  export const buildOpenRateAnalytics = (openRate) => ({
    email: { opened: `${openRate?.email_open_rate}%` },
    linkedin: { opened: `${openRate?.linkedin_open_rate}%` },
    whatsapp: { opened: `${openRate?.whatsapp_open_rate}%` },
  });
  
  // build reply rate analytics
  export const buildReplyRateAnalytics = (replyRate) => ({
    email: { responded: `${replyRate?.email_reply_rate}%` },
    linkedin: { responded: `${replyRate?.linkedin_reply_rate}%` },
    whatsapp: { responded: `${replyRate?.whatsapp_reply_rate}%` },
  });


  // channel icons
export const CHANNEL_ICONS = {
  email: Mail,
  linkedin: Linkedin,
  whatsapp: MessageCircle,
  voice: Phone,
};

// clamp to0–100 for bar width and threshold colors
export const clampForBar = (n) =>
  Math.min(100, Math.max(0, Number.isFinite(Number(n)) ? Number(n) : 0));

// format percentage
export const formatPct = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0%";
  return `${Math.round(v)}%`;
};

// data view options
export const DATA_VIEW_OPTIONS = [
  {
    label: "Last 7 Days",
    value: "last_7_days",
  },
  {
    label: "Last 14 Days",
    value: "last_14_days",
  },
  {
    label: "Last 30 Days",
    value: "last_30_days",
  },
  {
    label: "Last 90 Days",
    value: "last_90_days",
  },
];

// user dropdown options
export const USER_DROPDOWN_OPTIONS = [
  {
    label: "My Dashboard",
    value: "my_dashboard",
  },
  {
    label: "Team Dashboard",
    value: "team_dashboard",
  },
  {
    label: "Mudit",
    value: "mudit",
  },
];

// days map
export const DAYS_MAP = {
  last_7_days: 7,
  last_14_days: 14,
  last_30_days: 30,
  last_90_days: 90,
};

// top engaged columns
export const TOP_ENGAGED_COLUMNS = [
  {
    uid: "company_name",
    name: "Account",
    sortable: true,
    align: "left",
    width: "200px",
  },
  {
    uid: "signal_count",
    name: "Score",
    sortable: true,
    align: "center",
    width: "76px",
  },
  {
    uid: "engagement_temperature",
    name: "Temperature",
    sortable: true,
    align: "center",
    width: "108px",
  },
  {
    uid: "signals_by_channel",
    name: "Channels",
    sortable: false,
    align: "center",
    width: "152px",
  },
  {
    uid: "score_breakdown",
    name: "Score breakdown",
    sortable: false,
    align: "left",
    width: "285px",
  },
  {
    uid: "last_signal_at",
    name: "Last signal",
    sortable: true,
    align: "left",
    width: "128px",
  },
];

// fresh intent columns
export const FRESH_INTENT_COLUMNS = [
  {
    uid: "company_name",
    name: "Account",
    sortable: true,
    align: "left",
    width: "220px",
  },
  {
    uid: "signal_label",
    name: "Event",
    sortable: true,
    align: "left",
    width: "260px",
  },
  {
    uid: "channel",
    name: "Channel",
    sortable: true,
    align: "center",
    width: "110px",
  },
  {
    uid: "signal_updated_at",
    name: "Updated",
    sortable: true,
    align: "left",
    width: "160px",
  },
];

// dormant columns
export const DORMANT_COLUMNS = [
  {
    uid: "company_name",
    name: "Account",
    sortable: true,
    align: "left",
    width: "200px",
  },
  {
    uid: "account_status",
    name: "Status",
    sortable: true,
    align: "center",
    width: "150px",
  },
  {
    uid: "days_since_last_meaningful_engagement",
    name: "Days inactive",
    sortable: true,
    align: "center",
    width: "110px",
  },
  {
    uid: "last_meaningful_engagement_at",
    name: "Last meaningful engagement",
    sortable: true,
    align: "left",
    width: "180px",
  },
  {
    uid: "opportunity_progression",
    name: "Opportunity",
    sortable: true,
    align: "center",
    width: "120px",
  },
  {
    uid: "recent_buying_signal",
    name: "Buying signal",
    sortable: true,
    align: "center",
    width: "120px",
  },
];

export const SIMILAR_ACCOUNT_COLUMNS = [
  { uid: "company_name", name: "Account", sortable: true, align: "left", width: "200px" },
  { uid: "matched_account", name: "Matched from", sortable: true, align: "left", width: "180px" },
  { uid: "reference_score", name: "Reference score", sortable: true, align: "left", width: "130px" },
  { uid: "company_size", name: "Size", sortable: true, align: "left", width: "110px" },
  { uid: "location", name: "Location", sortable: true, align: "left", width: "150px" },
  { uid: "last_fetched_at", name: "Fetched on", sortable: true, align: "left", width: "130px" },
  { uid: "reason", name: "Why this account", sortable: false, align: "left", width: "300px" },
  { uid: "actions", name: "Action", sortable: false, align: "center", width: "80px" },
];

export const FOLLOW_UP_COLUMNS = [
  { uid: "company_name", name: "Account", sortable: true, align: "left", width: "200px" },
  { uid: "follow_up_type", name: "Type", sortable: true, align: "left", width: "130px" },
  { uid: "priority", name: "Priority", sortable: true, align: "center", width: "78px" },
  { uid: "contact_name", name: "Contact", sortable: true, align: "left", width: "160px" },
  { uid: "channel", name: "Channel", sortable: true, align: "left", width: "100px" },
  { uid: "reason", name: "Reason", sortable: false, align: "left", width: "300px" },
  { uid: "scheduled_at", name: "Scheduled", sortable: true, align: "left", width: "170px" },
];


// normalize top engaged accounts
export const normalizeTopEngaged = (accounts) => {
  return (accounts ?? []).map((account, index) => {
    return {
      id: String(
        account?.account_id ?? `te-${index}-${account?.company_name ?? ""}`,
      ),
      company_name: account?.company_name ?? "—",
      company_logo: account?.company_logo,
      signal_count: Number(account?.engagement_score ?? account?.signal_count) || 0,
      engagement_temperature: account?.engagement_temperature || "cold",
      last_signal_at: account?.last_signal_at ?? "",
      _raw: account,
    };
  });
}

// normalize fresh intent signals
export const normalizeFreshIntent = (signals) => {
  return (signals ?? []).map((signal, index) => {
    const acc = signal?.account ?? {};
    const objectType = signal?.signals?.result?.object;
    return {
      id: String(
        acc?.account_id ??
          `fi-${index}-${signal?.signal_updated_at ?? ""}-${acc?.company_name ?? ""}`,
      ),
      company_name: acc?.company_name ?? "—",
      company_logo: acc?.company_logo,
      signal_label: formatSignalType(signal?.channel, objectType),
      channel: signal?.channel ? String(signal.channel) : "—",
      signal_updated_at: signal?.signal_updated_at ?? "",
      _raw: signal,
    };
  });
}

// normalize dormant accounts
export const normalizeDormant = (accounts) => {
  return (accounts ?? []).map((account, index) => ({
    id: String(
      account?.account_id ?? `do-${index}-${account?.company_name ?? ""}`,
    ),
    company_name: account?.company_name ?? "—",
    company_logo: account?.company_logo,
    account_status: account?.account_status ?? "DORMANT",
    days_since_last_meaningful_engagement:
      Number(account?.days_since_last_meaningful_engagement) || 0,
    last_meaningful_engagement_at:
      account?.last_meaningful_engagement_at ?? "",
    opportunity_progression: account?.opportunity_progression ?? "NONE",
    recent_buying_signal: account?.recent_buying_signal ?? "LOW",
    last_activity_at: account?.last_activity_at ?? "",
    last_signal_at: account?.last_signal_at ?? "",
    _raw: account,
  }));
};

export const normalizeSimilarAccounts = (accounts) =>
  (accounts ?? []).map((account, index) => ({
    id: String(account?.company_domain || `similar-${index}`),
    company_name: account?.company_name ?? "—",
    company_logo: account?.company_logo,
    matched_account: account?.matched_account ?? "—",
    reference_score: account?.reference_score ?? "—",
    company_size: account?.company_size ?? "—",
    location: account?.location ?? "—",
    last_fetched_at: account?.last_fetched_at ?? null,
    reason: account?.reason ?? "—",
    _raw: account,
  }));

export const normalizeFollowUps = (followUps) =>
  (followUps ?? []).map((followUp, index) => ({
    id: String(
      `${followUp?.follow_up_id || `follow-up-${index}`}-${followUp?.follow_up_type || followUp?.item_type || "scheduled"}`,
    ),
    company_name: followUp?.company_name ?? "—",
    company_logo: followUp?.company_logo,
    follow_up_type: followUp?.follow_up_type ?? "scheduled",
    priority: Number(followUp?.priority) || 3,
    contact_name: followUp?.contact_name ?? "—",
    channel: followUp?.channel ?? "—",
    reason: followUp?.reason ?? followUp?.notes ?? "Follow up with stakeholder",
    recommended_action: followUp?.recommended_action ?? "View Follow Up",
    notes: followUp?.notes ?? "Follow up with stakeholder",
    scheduled_at: followUp?.scheduled_at ?? "",
    detected_at: followUp?.detected_at ?? "",
    _raw: followUp,
  }));
